import React, { useMemo } from "react";
import { alpha, Box, Chip, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ReportDTO from "../../../../models/finance/Reports";
import { useCurrencyFormatter } from "../../../../contexts/CurrencyContext";
import { useUserProfileContext } from "../../../../contexts/ProfileContext";

interface MonthlyReportsCombinedTrendProps {
  report: ReportDTO;
}

const toEntries = (values: unknown): Array<[string, number]> => {
  if (values instanceof Map) {
    return Array.from(values.entries()).map(([key, value]) => [String(key), Number(value) || 0]);
  }

  if (!values || typeof values !== "object") {
    return [];
  }

  return Object.entries(values as Record<string, unknown>).map(([key, value]) => [key, Number(value) || 0]);
};

const toDayKey = (dateLike: string): string | null => {
  const parsed = new Date(dateLike);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const MonthlyReportsCombinedTrendComponent: React.FC<MonthlyReportsCombinedTrendProps> = ({ report }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { formatCompactCurrencyNumber, formatCurrencyNumber } = useCurrencyFormatter();
  const { formatDate } = useUserProfileContext();

  const chartData = useMemo(() => {
    const expensesByDay = new Map<string, number>();
    const incomesByDay = new Map<string, number>();

    toEntries(report.reportInfo.expensesValuePerDay).forEach(([rawKey, value]) => {
      const dayKey = toDayKey(rawKey);
      if (!dayKey) return;
      expensesByDay.set(dayKey, value);
    });

    toEntries(report.reportInfo.incomesValuePerDay).forEach(([rawKey, value]) => {
      const dayKey = toDayKey(rawKey);
      if (!dayKey) return;
      incomesByDay.set(dayKey, value);
    });

    const allDayKeys = Array.from(new Set([...expensesByDay.keys(), ...incomesByDay.keys()])).sort((a, b) =>
      new Date(a).getTime() - new Date(b).getTime()
    );

    return allDayKeys.map((dayKey) => {
      const date = new Date(dayKey);
      const expense = Number(expensesByDay.get(dayKey) || 0);
      const income = Number(incomesByDay.get(dayKey) || 0);
      const net = income - expense;

      return {
        date: formatDate(date),
        day: String(date.getDate()),
        dateTs: date.getTime(),
        income,
        expense,
        net,
      };
    });
  }, [report, formatDate]);

  const stats = useMemo(() => {
    const netValues = chartData.map((d) => d.net);
    const totalIncome = chartData.reduce((sum, curr) => sum + curr.income, 0);
    const totalExpense = chartData.reduce((sum, curr) => sum + curr.expense, 0);
    const netTotal = totalIncome - totalExpense;
    const expenseDays = chartData.filter((entry) => entry.expense > 0).length;
    const incomePeakDay = chartData.reduce(
      (best, curr) => (curr.income > best.income ? curr : best),
      chartData[0] || { date: "-", income: 0 }
    );
    const expensePeakDay = chartData.reduce(
      (best, curr) => (curr.expense > best.expense ? curr : best),
      chartData[0] || { date: "-", expense: 0 }
    );
    const avgNet = netValues.length ? netValues.reduce((sum, val) => sum + val, 0) / netValues.length : 0;
    const bestDay = chartData.reduce(
      (best, curr) => (curr.net > best.net ? curr : best),
      chartData[0] || { date: "-", net: 0 }
    );
    const spendToIncomeRatio = totalIncome > 0 ? (totalExpense / totalIncome) * 100 : null;

    return {
      avgNet,
      bestDay,
      incomePeakDay,
      expensePeakDay,
      totalIncome,
      totalExpense,
      netTotal,
      spendToIncomeRatio,
      expenseDays,
      activeDays: chartData.length,
    };
  }, [chartData]);

  if (chartData.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No daily cashflow data
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add income and expense records to view your combined timeline.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={2} sx={{ mb: 2.2 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.6, borderRadius: 2, border: `1px solid ${alpha(theme.palette.info.main, 0.28)}`, background: alpha(theme.palette.info.main, 0.1), textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">Total income</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.success.main, wordBreak: "break-word" }}>
              {isMobile ? formatCompactCurrencyNumber(stats.totalIncome) : formatCurrencyNumber(stats.totalIncome)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.6, borderRadius: 2, border: `1px solid ${alpha(theme.palette.error.main, 0.28)}`, background: alpha(theme.palette.error.main, 0.1), textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">Total expenses</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.error.main, wordBreak: "break-word" }}>
              {isMobile ? formatCompactCurrencyNumber(stats.totalExpense) : formatCurrencyNumber(stats.totalExpense)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.6, borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`, background: alpha(theme.palette.primary.main, 0.1), textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">Net outcome</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: stats.netTotal >= 0 ? theme.palette.success.main : theme.palette.error.main }}>
              {isMobile ? formatCompactCurrencyNumber(stats.netTotal) : formatCurrencyNumber(stats.netTotal)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.6, borderRadius: 2, border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`, background: alpha(theme.palette.warning.main, 0.1), textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">Expense intensity</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.warning.light }}>
              {stats.spendToIncomeRatio === null ? "N/A" : `${stats.spendToIncomeRatio.toFixed(0)}%`}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mb: 1.3, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Chip size="small" label="Income lane" sx={{ bgcolor: alpha(theme.palette.success.main, 0.14), color: theme.palette.success.light }} />
        <Chip size="small" label="Expense lane" sx={{ bgcolor: alpha(theme.palette.error.main, 0.14), color: theme.palette.error.light }} />
      </Box>

      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            border: `1px solid ${alpha(theme.palette.success.main, 0.24)}`,
            borderRadius: 2,
            p: { xs: 1.2, sm: 1.5 },
            mb: 2,
            background: alpha(theme.palette.success.main, 0.04),
          }}
        >
          <Typography variant="overline" sx={{ color: theme.palette.success.light, letterSpacing: "0.08em" }}>
            Income Timeline
          </Typography>
          <Box sx={{ height: { xs: 164, sm: 210 }, width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} syncId="cashflow-month" margin={isMobile ? { top: 8, right: 2, left: 0, bottom: 0 } : { top: 14, right: 16, left: 10, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.text.primary, 0.1)} />
              <XAxis
                dataKey="dateTs"
                type="number"
                scale="time"
                domain={["dataMin", "dataMax"]}
                hide
              />
              <YAxis
                tickFormatter={(value) => formatCompactCurrencyNumber(value)}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                axisLine={false}
                tickLine={false}
                width={isMobile ? 40 : 64}
              />
              <Tooltip
                contentStyle={{
                  background: alpha(theme.palette.background.paper, 0.97),
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                  borderRadius: 10,
                  color: theme.palette.text.primary,
                }}
                labelFormatter={(value: number) => formatDate(new Date(value))}
                formatter={(value: number) => [formatCurrencyNumber(Number(value)), "Income"]}
              />
              <Line
                type="monotone"
                dataKey="income"
                stroke={theme.palette.success.main}
                strokeWidth={isMobile ? 2.2 : 2.5}
                dot={isMobile && chartData.length <= 8}
                activeDot={{ r: isMobile ? 5 : 6 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        </Box>

        <Box
          sx={{
            border: `1px solid ${alpha(theme.palette.error.main, 0.24)}`,
            borderRadius: 2,
            p: { xs: 1.2, sm: 1.5 },
            background: alpha(theme.palette.error.main, 0.04),
          }}
        >
          <Typography variant="overline" sx={{ color: theme.palette.error.light, letterSpacing: "0.08em" }}>
            Expense Timeline
          </Typography>
          <Box sx={{ height: { xs: 190, sm: 250 }, width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} syncId="cashflow-month" margin={isMobile ? { top: 8, right: 2, left: 0, bottom: 12 } : { top: 8, right: 16, left: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.text.primary, 0.1)} />
              <XAxis
                dataKey="dateTs"
                type="number"
                scale="time"
                domain={["dataMin", "dataMax"]}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                axisLine={false}
                tickLine={false}
                minTickGap={isMobile ? 24 : 8}
                interval={isMobile ? "preserveStartEnd" : "preserveStartEnd"}
                tickFormatter={(value: number) => {
                  const date = new Date(value);
                  return isMobile ? String(date.getDate()) : formatDate(date);
                }}
              />
              <YAxis
                tickFormatter={(value) => formatCompactCurrencyNumber(value)}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                axisLine={false}
                tickLine={false}
                width={isMobile ? 40 : 64}
              />
              <Tooltip
                contentStyle={{
                  background: alpha(theme.palette.background.paper, 0.97),
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                  borderRadius: 10,
                  color: theme.palette.text.primary,
                }}
                labelFormatter={(value: number) => formatDate(new Date(value))}
                formatter={(value: number) => [formatCurrencyNumber(Number(value)), "Expense"]}
              />
              <Line
                type="monotone"
                dataKey="expense"
                stroke={theme.palette.error.main}
                strokeWidth={isMobile ? 2.2 : 2.5}
                dot={isMobile && chartData.length <= 8}
                activeDot={{ r: isMobile ? 5 : 6 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 1.8, display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
        <Chip size="small" label={`Best day: ${stats.bestDay.date}`} sx={{ bgcolor: alpha(theme.palette.success.main, 0.14), color: theme.palette.success.light }} />
        <Chip size="small" label={`Income peak: ${stats.incomePeakDay.date}`} sx={{ bgcolor: alpha(theme.palette.success.main, 0.14), color: theme.palette.success.light }} />
        <Chip size="small" label={`Expense peak: ${stats.expensePeakDay.date}`} sx={{ bgcolor: alpha(theme.palette.error.main, 0.14), color: theme.palette.error.light }} />
        <Chip size="small" label={`Expense days: ${stats.expenseDays}/${stats.activeDays}`} sx={{ bgcolor: alpha(theme.palette.info.main, 0.14), color: theme.palette.info.light }} />
      </Box>
    </Box>
  );
};

export default MonthlyReportsCombinedTrendComponent;
